/**
 * Camera control (R23): edge-scroll, arrow keys, and minimap navigation.
 * Rotation is locked.
 *
 * Panning accumulates in **whole internal pixels**. Sub-pixel camera motion is
 * what produces dither crawl, and R23 makes panning the normal case rather than
 * an edge case (KTD2).
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import { Renderer, SIDEBAR_WIDTH } from '../render/index.js'

/** Internal pixels per second at full pan speed. */
const PAN_SPEED = 260
/** How close to the viewport edge triggers edge-scroll, in CSS pixels. */
const EDGE_MARGIN = 12

export class CameraControl implements Subsystem {
  static id = 'camera'
  static deps = ['render'] as const

  private renderer!: Renderer

  /** Accumulated sub-pixel pan, flushed to the renderer in whole pixels. */
  private residualX = 0
  private residualY = 0

  private readonly keys = new Set<string>()
  private pointerX = -1
  private pointerY = -1
  private pointerInside = false
  /** Edge-scroll is suppressed while a selection drag is in progress. */
  edgeScrollEnabled = true

  init(ctx: Ctx): void {
    this.renderer = ctx.get<Renderer>('render')
  }

  onKeyDown(code: string): void {
    this.keys.add(code)
  }

  onKeyUp(code: string): void {
    this.keys.delete(code)
  }

  onPointerMove(clientX: number, clientY: number): void {
    this.pointerX = clientX
    this.pointerY = clientY
    this.pointerInside = true
  }

  onPointerLeave(): void {
    this.pointerInside = false
  }

  clearKeys(): void {
    this.keys.clear()
  }

  update(dt: number): void {
    let dx = 0
    let dy = 0

    // Arrows and edge-scrolling only, which is how Red Alert 2 does it.
    //
    // W/A/S/D was tried and reverted. Panning with the left hand sounds like an
    // upgrade until you notice what it costs: A, S and D are attack-move, stop
    // and deploy, and there is nowhere as good to put them. Deploy in
    // particular is the first thing a new player is ever told to press, and
    // moving it broke the one instruction the game gives them.
    if (this.keys.has('ArrowLeft')) dx -= 1
    if (this.keys.has('ArrowRight')) dx += 1
    if (this.keys.has('ArrowUp')) dy += 1
    if (this.keys.has('ArrowDown')) dy -= 1

    if (this.edgeScrollEnabled && this.pointerInside && this.isInsideViewport(this.pointerX, this.pointerY)) {
      const layout = this.renderer.getLayout()
      // Edge-scroll triggers at the **unoccluded viewport edge**, not the window
      // edge, and only while the pointer is actually over the world.
      //
      // The vertical tests used to check Y alone, with a trailing guard that
      // clamped `dx` and left `dy` untouched. So the bottom zone spanned the
      // full width of the window including the sidebar, and reaching down it
      // for the repair and sell buttons scrolled the map out from under you —
      // which is exactly how it was reported: the buttons were hard to click
      // because the map moved while you aimed at them.
      const left = layout.rect.x
      const right = layout.rect.x + layout.rect.width
      const top = layout.rect.y
      const bottom = layout.rect.y + layout.rect.height

      if (this.pointerX < left + EDGE_MARGIN) dx -= 1
      if (this.pointerX > right - EDGE_MARGIN) dx += 1
      if (this.pointerY < top + EDGE_MARGIN) dy += 1
      if (this.pointerY > bottom - EDGE_MARGIN) dy -= 1
    }

    if (dx === 0 && dy === 0) {
      this.residualX = 0
      this.residualY = 0
      return
    }

    // Normalise so diagonal panning is not faster than orthogonal.
    const len = Math.hypot(dx, dy)
    this.residualX += (dx / len) * PAN_SPEED * dt
    this.residualY += (dy / len) * PAN_SPEED * dt

    const stepX = Math.trunc(this.residualX)
    const stepY = Math.trunc(this.residualY)
    if (stepX !== 0 || stepY !== 0) {
      this.residualX -= stepX
      this.residualY -= stepY
      this.renderer.panBy(stepX, stepY)
    }
  }

  /** Centre on a world cell. Used by minimap clicks and centre-on-selection. */
  centerOn(cellX: number, cellY: number): void {
    this.renderer.centerOn(cellX, cellY)
  }

  /** True if a client point lies inside the world viewport (not the sidebar). */
  isInsideViewport(clientX: number, clientY: number): boolean {
    const layout = this.renderer.getLayout()
    return (
      clientX >= layout.rect.x &&
      clientX < layout.rect.x + layout.rect.width &&
      clientY >= layout.rect.y &&
      clientY < layout.rect.y + layout.rect.height
    )
  }

  /** Visible world extent in cells, for the minimap's viewport rectangle. */
  viewportCells(): { width: number; height: number } {
    const basis = this.renderer.basis
    const layout = this.renderer.getLayout()
    return {
      width: basis.stepRight.length() * layout.internalWidth,
      height: basis.stepUp.length() * layout.internalHeight,
    }
  }
}

export { SIDEBAR_WIDTH }
